import { useEffect, useState } from "react";
import { api, defaultSettings, invalidateSiteCache } from "@/api";
import { toast } from "sonner";
import UploadField from "@/components/admin/UploadField";
import AdminPassword from "@/components/admin/AdminPassword";

const inputCls = "mt-2 w-full border-b border-bark/40 bg-transparent py-2.5 outline-none transition-[border-color] duration-300 focus:border-clay";

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.25em] text-taupe">{label}</label>
    {children}
  </div>
);

const AdminSettingsForm = () => {
  const [form, setForm] = useState(null);
  const [villesText, setVillesText] = useState("");

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = { ...defaultSettings, ...r.data, legal: { ...defaultSettings.legal, ...(r.data.legal || {}) } };
      setForm(s);
      setVillesText((s.villes || []).join(", "));
    });
  }, []);

  if (!form) return <p className="text-bark">Chargement…</p>;

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, villes: villesText.split(",").map((v) => v.trim()).filter(Boolean) };
    try {
      await api.put("/admin/settings", payload);
      invalidateSiteCache();
      toast.success("Réglages enregistrés — visibles immédiatement sur le site");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enregistrement impossible");
    }
  };

  return (
    <form onSubmit={save} className="max-w-2xl space-y-10" data-testid="admin-settings-form">
      <section className="space-y-6">
        <h2 className="font-serif text-2xl tracking-tight">Coordonnées affichées</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="Téléphone">
            <input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className={inputCls} data-testid="settings-input-telephone" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="settings-input-email" />
          </Field>
        </div>
        <Field label="Zone d'intervention">
          <input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className={inputCls} data-testid="settings-input-zone" />
        </Field>
        <Field label="Communes couvertes (séparées par des virgules)">
          <textarea rows={3} value={villesText} onChange={(e) => setVillesText(e.target.value)} className={`${inputCls} resize-none`} data-testid="settings-input-villes" />
        </Field>
      </section>

      <section className="space-y-6">
        <h2 className="font-serif text-2xl tracking-tight">Photo du haut de l'accueil</h2>
        <UploadField label="Image d'accueil" value={form.hero} onChange={(v) => setForm({ ...form, hero: v })} testid="settings-upload-hero" />
      </section>

      <section className="space-y-6">
        <h2 className="font-serif text-2xl tracking-tight">Mentions légales</h2>
        <Field label="Forme juridique">
          <input value={form.legal.formeJuridique} onChange={(e) => setForm({ ...form, legal: { ...form.legal, formeJuridique: e.target.value } })} className={inputCls} data-testid="settings-input-forme" />
        </Field>
        <div className="grid gap-6 sm:grid-cols-2">
          <Field label="SIRET">
            <input value={form.legal.siret} onChange={(e) => setForm({ ...form, legal: { ...form.legal, siret: e.target.value } })} className={inputCls} data-testid="settings-input-siret" />
          </Field>
          <Field label="Adresse du siège">
            <input value={form.legal.adresse} onChange={(e) => setForm({ ...form, legal: { ...form.legal, adresse: e.target.value } })} className={inputCls} data-testid="settings-input-adresse" />
          </Field>
        </div>
      </section>

      <button type="submit" data-testid="settings-save" className="rounded-full bg-clay px-10 py-4 text-sm font-medium text-cream transition-colors duration-300 hover:bg-ink">
        Enregistrer les réglages
      </button>
    </form>
  );
};

const AdminSettingsPage = () => (
  <>
    <AdminSettingsForm />
    <div className="mt-6">
      <AdminPassword />
    </div>
  </>
);

export default AdminSettingsPage;
